// ProtectedRoute.tsx - Componente para proteger rutas que requieren autenticación
import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Layout from '../../components/Layout';
import AgentShell from '../../components/agent/AgentShell';

interface ProtectedRouteProps {
    children: ReactNode;
    redirectTo?: string;
}

// Portales propios: estos roles solo usan sus pantallas (el resto es del personal)
const ROLE_HOME: Record<string, string> = {
    courier: '/courier/',
    client: '/client/',
};

const ProtectedRoute = ({ children, redirectTo = '/login' }: ProtectedRouteProps) => {
    const { user, userData, loading } = useAuth();
    const location = useLocation();

    // Mostrar loading mientras se verifica la autenticación
    if (loading) {
        return (
            <div className="loading-container" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: '#f3f4f6'
            }}>
                <div className="loading-spinner" style={{
                    width: '48px',
                    height: '48px',
                    border: '4px solid #e5e7eb',
                    borderTopColor: '#E50914',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }} />
                <style>{`
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Si no está autenticado, redirigir a login
    if (!user) {
        // Guardar la ubicación actual para redirigir después del login
        return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }

    // Un courier o un cliente que abre una pantalla del personal va a su propio inicio
    // (antes, al iniciar sesión todos caían en el panel de administración).
    const home = userData ? ROLE_HOME[userData.role] : undefined;
    if (home && !location.pathname.startsWith(home)) {
        return <Navigate to={`${home}dashboard`} replace />;
    }

    // La app del courier trae su propio marco (encabezado y barra inferior)
    // El portal de clientes también (menú lateral blanco en computadora, barra inferior en celular)
    if (location.pathname.startsWith('/courier/') || (location.pathname.startsWith('/client/') && location.pathname !== '/client/register')) {
        return <>{children}</>;
    }

    // Portal comercial: marco propio (diseño Platform 2.0)
    if (location.pathname.startsWith('/agent/')) {
        return <AgentShell>{children}</AgentShell>;
    }

    // Si está autenticado, mostrar el contenido con Layout (incluye Sidebar)
    return <Layout>{children}</Layout>;
};

export default ProtectedRoute;
